<template>
  <div class="similar-priced-cars" @click.stop>
    <div class="similar-priced-cars__container">
      <!-- 标题栏 -->
      <div class="similar-priced-cars__title">
        <div class="similar-priced-cars__heading">Similar Priced Cars</div>
        <a :href="allCarsLink" class="similar-priced-cars__view">
          <div class="similar-priced-cars__view-all">View All</div>
          <img src="@/assets/view-all.svg" class="similar-priced-cars__icon" alt="">
        </a>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading && cars.length === 0" class="similar-priced-cars__loading">
        <div class="similar-priced-cars__loading-text">Loading...</div>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="similar-priced-cars__error">
        <div class="similar-priced-cars__error-text">{{ error }}</div>
      </div>

      <!-- 无数据状态 -->
      <div v-else-if="cars.length === 0" class="similar-priced-cars__empty">
        <div class="similar-priced-cars__empty-text">No similar cars found</div>
      </div>

      <!-- 卡片容器 - PC版用flex-wrap，移动版用横向滚动 -->
      <div v-else class="similar-priced-cars__cards"
        :class="{'similar-priced-cars__cards--mobile': deviceType === 'mobile'}">
        <!-- 单个卡片 -->
        <a v-for="(car, index) in cars" :key="`car-${index}`" class="similar-priced-cars__card"
          :href="getCarDetailUrl ? getCarDetailUrl(car) : getDetailLink(car)">
          <!-- 车辆图片 -->
          <div class="similar-priced-cars__image-container">
            <img :src="car.image" class="similar-priced-cars__pic" alt="">
            <div class="similar-priced-cars__speed-tag">
              <div class="similar-priced-cars__speed-value">
                <div class="similar-priced-cars__speed-number">{{ car.speedValue }}</div>
              </div>
              <div class="similar-priced-cars__speed-label">
                <div class="similar-priced-cars__speed-text">0–60-MPH Times</div>
              </div>
            </div>
          </div>

          <!-- 车辆信息 -->
          <div class="similar-priced-cars__baseinfo">
            <div class="similar-priced-cars__info">
              <!-- 车型和品牌 -->
              <div class="similar-priced-cars__card-title">
                <div class="similar-priced-cars__name">
                  <div class="similar-priced-cars__model">{{ car.model }}</div>
                  <div class="similar-priced-cars__brand">{{ car.brand }}</div>
                </div>
                <img :src="car.logo" class="similar-priced-cars__logo" alt="">
              </div>

              <!-- 信息分隔线 -->
              <div class="similar-priced-cars__divider"></div>

              <!-- 规格信息 -->
              <div class="similar-priced-cars__type">
                <div class="similar-priced-cars__type-item">
                  <img src="@/assets/calendar.svg" class="similar-priced-cars__type-icon" alt="">
                  <div class="similar-priced-cars__type-text">{{ car.year }}</div>
                </div>
                <div class="similar-priced-cars__type-item">
                  <img src="@/assets/purpose.svg" class="similar-priced-cars__type-icon" alt="">
                  <div class="similar-priced-cars__type-text">{{ car.category }}</div>
                </div>
                <div class="similar-priced-cars__type-item">
                  <img src="@/assets/car-type.svg" class="similar-priced-cars__type-icon" alt="">
                  <div class="similar-priced-cars__type-text">{{ car.bodyType }}</div>
                </div>
              </div>

              <!-- 信息分隔线 -->
              <div class="similar-priced-cars__divider"></div>

              <!-- 价格 -->
              <div class="similar-priced-cars__price">
                <div class="similar-priced-cars__price-amount">{{ car.price }}</div>
                <div class="similar-priced-cars__price-link">
                  <img src="@/assets/arrow_outward.svg" alt="">
                </div>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'SimilarPricedCars',
  props: {
    carBrand: {
      type: String,
      default: ''
    },
    carCategory: {
      type: String,
      default: ''
    },
    pageNum: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 8
    },
    carList: {
      type: Array,
      default: () => []
    },
    getCarDetailUrl: {
      type: Function,
      default: null
    }
  },
  computed: {
    ...mapGetters([
      'deviceType'
    ]),
    allCarsLink () {
      const currentChannel = this.$route.params.channel;
      const query = this.$route.query;
      const queryString = Object.keys(query).length > 0 ? `?${new URLSearchParams(query).toString()}` : '';
      let path = '/car-list';
      if (currentChannel) {
        path = `/${currentChannel}${path}`;
      }
      return `${path}${queryString}`;
    }
  },
  data () {
    return {
      cars: [],
      loading: false,
      error: null
    }
  },
  mounted () {
    // 如果提供了 carList 属性，直接使用它
    if (this.carList && this.carList.length > 0) {
      console.log('[SimilarPricedCars] 使用传入的 carList 数据:', this.carList.length);
      this.cars = this.carList;
    } else {
      // 否则通过 API 获取数据
      this.fetchSimilarCars();
    }
  },
  watch: {
    // 监听 carList 变化，更新组件数据
    carList: {
      handler (newVal) {
        if (newVal && newVal.length > 0) {
          console.log('[SimilarPricedCars] carList 更新:', newVal.length);
          this.cars = newVal;
          this.loading = false;
          this.error = null;
        }
      },
      immediate: true
    }
  },
  methods: {
    // 获取相似价格的车辆
    async fetchSimilarCars () {
      if (!this.carBrand && !this.carCategory) {
        console.warn('[SimilarPricedCars] 缺少查询参数 brand 或 category');
        return;
      }

      try {
        this.loading = true;
        this.error = null;

        // 构建API参数 - 使用props传入的分页参数
        const params = {
          pageSize: this.pageSize,
          pageNum: this.pageNum,
          total: 30  // 添加total参数，默认值为30
        };

        // 添加品牌参数(如果有)
        if (this.carBrand) {
          params.brand = this.carBrand;
        }

        // // 添加类型参数(如果有)
        // if (this.carCategory) {
        //   params.type = this.carCategory;
        // }

        console.log(`[SimilarPricedCars] 查询参数: pageNum=${this.pageNum}, pageSize=${this.pageSize}, brand=${this.carBrand}, total=30`);

        // 从store获取域名配置的主题过滤条件
        const themeFilters = this.$store.getters.domainConfig?.themeFilters || {};

        // 合并主题过滤参数
        const requestParams = { ...params, ...themeFilters };

        // 调用searchCar接口
        const response = await this.$global.server.searchCar(requestParams);

        if (response && response.code === 200 && response.data && response.data.list) {
          // 处理API返回的数据
          this.cars = response.data.list.map(this.formatCarData);
          console.log(`[SimilarPricedCars] 获取到相似车辆: pageNum=${this.pageNum}, count=${this.cars.length}`);
        } else {
          console.warn('[SimilarPricedCars] 获取数据失败:', response?.message || '未知错误');
          this.cars = [];
        }
      } catch (error) {
        console.error('[SimilarPricedCars] 获取相似车辆数据出错:', error);
        this.error = error.message || '获取数据失败';
        this.cars = [];
      } finally {
        this.loading = false;
      }
    },

    // 格式化车辆数据
    formatCarData (item) {
      // 格式化车辆数据以便在页面上显示
      const formattedItem = { ...item };
      formattedItem.logo = item.logoUrl || require('@/assets/car-logo.svg');

      // // 处理图片
      // if (item.photos && item.photos.length > 0) {
      //   // 如果photos是字符串（逗号分隔的URL列表），则转换为数组
      //   if (typeof item.photos === 'string') {
      //     const photoArray = item.photos.split(',');
      //     formattedItem.image = photoArray[0]; // 使用第一张图片作为主图
      //   } else {
      //     formattedItem.image = item.photos[0];
      //   }
      // } else {
      //   // 使用默认图片
      //   formattedItem.image = require('@/assets/car-recommend-img.png');
      // }

      formattedItem.image = item.cover;

      // 处理模型名称
      if (!formattedItem.model && item.name) {
        formattedItem.model = item.name;
      }

      // 确保category和bodyType有值
      formattedItem.category = item.purpose || item.type || 'N/A';
      formattedItem.bodyType = item.type || formattedItem.category || 'N/A';

      // 处理年份
      formattedItem.year = item.year || item.marketTime?.substring(0, 4) || '-';

      // 处理价格
      formattedItem.price = this.formatPrice(item.leastPrice, item.mostPrice) || item.price || '-';

      // 处理加速时间
      formattedItem.speedValue = item.accelerationTime ? `${item.accelerationTime}s` : '-';

      return formattedItem;
    },

    // 格式化价格显示
    formatPrice (leastPrice, mostPrice) {
      if (!leastPrice && !mostPrice) return null;

      const formatNumber = (num) => {
        if (!num) return null;
        // 将价格格式化为$xx,xxx格式
        return '$' + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      };

      if (leastPrice && mostPrice) {
        // 有最低价和最高价，显示价格区间
        return `${formatNumber(leastPrice)}-${formatNumber(mostPrice)}`;
      } else if (leastPrice) {
        // 只有最低价
        return `>${formatNumber(leastPrice)}`;
      } else if (mostPrice) {
        // 只有最高价
        return `<${formatNumber(mostPrice)}`;
      }

      return null;
    },

    getDetailLink (car) {
      try {
        if (!car || !car.id) {
          return '#';
        }
        const currentChannel = this.$route.params.channel;
        const formattedName = encodeURIComponent((car.model || car.name || '').replace(/\s+/g, '-'));
        const query = this.$route.query;
        const queryString = Object.keys(query).length > 0 ? `?${new URLSearchParams(query).toString()}` : '';

        let path = `/cars/${formattedName}/${car.id}`;
        if (currentChannel) {
          path = `/${currentChannel}${path}`;
        }
        return `${path}${queryString}`;
      } catch (error) {
        console.error('[SimilarPricedCars] Detail link generation error:', error);
        return '#';
      }
    }
  }
}
</script>

<style lang="scss" >
@import '~@/css/variables.scss';

.similar-priced-cars {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	align-self: stretch;
	position: relative;
	width: 100%;
	padding: rem(50) 0;
	background: #f9fbfc;

	&__container {
		display: flex;
		flex-direction: column;
		gap: rem(20);
		align-items: flex-start;
		justify-content: flex-start;
		width: 100%;
		max-width: rem(1200);
		margin: 0 auto;
		padding: 0 rem(20);
		position: relative;
	}

	&__title {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		align-self: stretch;
		position: relative;
	}

	&__heading {
		color: #050b20;
		text-align: left;

		font-size: rem(30);
		line-height: rem(30);
		font-weight: 700;
		position: relative;
	}

	&__view {
		display: flex;
		align-items: center;
		gap: rem(10);
		cursor: pointer;
	}

	&__view-all {
		font-size: rem(16);
		line-height: rem(16);
		font-weight: 500;
		color: #405ff2;
	}

	&__icon {
		flex-shrink: 0;
		width: rem(14);
		height: rem(14);
		position: relative;
		overflow: visible;
	}

	&__cards {
		display: flex;
		flex-wrap: wrap;
		gap: rem(20);
		align-self: stretch;
		width: 100%;
		min-height: rem(300);

		// 移动端横向滚动样式
		&--mobile {
			flex-wrap: nowrap;
			overflow-x: auto;
			scrollbar-width: none; /* Firefox */
			-ms-overflow-style: none; /* IE and Edge */
			padding-bottom: rem(10);

			&::-webkit-scrollbar {
				display: none; /* Chrome, Safari, Opera */
			}
		}
	}

	&__card {
		// width: calc(33.33% - rem(14));
		flex: 0 0 calc((100% - rem(120)) / 4); /* 使每个子元素占1/4的宽度 */
		border-radius: rem(12);
		background: #fff;
		overflow: hidden;
		border: 1px solid #e9e9e9;

		// 移动端卡片样式
		.similar-priced-cars__cards--mobile & {
			width: rem(280);
			flex: 0 0 auto;
		}
	}

	&__image-container {
		width: 100%;
		height: rem(180);
		position: relative;
		overflow: hidden;
	}

	&__pic {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__speed-tag {
		position: absolute;
		top: rem(6);
		left: rem(6);
		z-index: 2;
		display: flex;
		background: rgba(0, 0, 0, 0.5);
		border-radius: rem(8);
		overflow: hidden;
	}

	&__speed-value {
		background: rgba(0, 0, 0, 0.5);
		padding: rem(4) rem(8);
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
	}

	&__speed-number {
		color: #ffffff;
		text-align: center;
		font-size: rem(12);
		line-height: rem(12);
		font-weight: 700;
	}

	&__speed-label {
		padding: rem(4) rem(8);
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
	}

	&__speed-text {
		color: #ffffff;
		text-align: center;
		font-size: rem(10);
		line-height: rem(10);
		font-weight: 400;
	}

	&__baseinfo {
		background: rgba(255, 255, 255, 0.5);
		backdrop-filter: blur(10px);
		border-radius: rem(12);
		padding: rem(16);
		display: flex;
		flex-direction: column;
		gap: rem(10);
		align-items: flex-start;
		justify-content: flex-start;
		width: 100%;
		z-index: 2;
	}

	&__info {
		display: flex;
		flex-direction: column;
		gap: rem(16);
		align-items: flex-start;
		justify-content: flex-start;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__card-title {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__name {
		display: flex;
		flex-direction: column;
		gap: rem(8);
		align-items: flex-start;
		justify-content: flex-start;
		position: relative;
	}

	&__model {
		color: #050b20;
		font-size: rem(16);
		font-style: normal;
		font-weight: 700;
		line-height: 100%; /* 16px */
		margin-bottom: rem(4);
		display: flex;
		align-items: center;
		justify-content: flex-start;
		overflow: hidden;
		display: -webkit-box;
		text-overflow: ellipsis;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
	}

	&__brand {
		color: #848998;
		font-size: rem(12);
		font-style: normal;
		font-weight: 400;
		line-height: 100%; /* 12px */
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	&__logo {
		width: rem(36);
		height: rem(36);
		background: #fff;
		border-radius: 50%;
		padding: rem(2);
		object-fit: contain;
	}

	&__divider {
		background: #050b20;
		opacity: 0.1;
		align-self: stretch;
		flex-shrink: 0;
		height: rem(1);
		position: relative;
	}

	&__type {
		display: flex;
		flex-direction: column;
		gap: rem(8);
		align-items: flex-start;
		justify-content: flex-start;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__type-item {
		display: flex;
		flex-direction: row;
		gap: rem(9);
		align-items: center;
		justify-content: flex-start;
		flex-shrink: 0;
		position: relative;
	}

	&__type-icon {
		flex-shrink: 0;
		width: rem(12);
		height: rem(12);
		position: relative;
	}

	&__type-text {
		color: #050b20;
		text-align: left;
		font-size: rem(12);
		line-height: rem(12);
		font-weight: 400;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	&__price {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__price-amount {
		color: #050b20;
		text-align: left;
		font-size: rem(18);
		line-height: rem(18);
		font-weight: 700;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	&__price-link {
		border-radius: rem(50%);
		width: rem(16);
		height: rem(16);
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;

		img {
			flex-shrink: 0;
			width: rem(16);
			height: rem(16);
			position: relative;
			overflow: visible;
		}
	}

	&__loading {
		display: flex;
		justify-content: center;
		align-items: center;
		height: rem(200);
	}

	&__loading-text {
		font-size: rem(16);
		font-weight: 500;
		color: #405ff2;
	}

	&__error {
		display: flex;
		justify-content: center;
		align-items: center;
		height: rem(200);
	}

	&__error-text {
		font-size: rem(16);
		font-weight: 500;
		color: #ff0000;
	}

	&__empty {
		display: flex;
		justify-content: center;
		align-items: center;
		height: rem(200);
	}

	&__empty-text {
		font-size: rem(16);
		font-weight: 500;
		color: #4c5164;
	}
}

// 响应式样式 - PC设备
.detect_deviece_pc,
.detect_deviece_desktop {
	.similar-priced-cars {
		--fontsize-base: #{$vw_fontsize_pc};
		font-size: calc(var(--fontsize-base) / #{$vw_design_pc} * 100vw);
		padding: rem(50) 0;
	}
}

// 响应式样式 - 移动设备
.detect_deviece_mobile {
	.similar-priced-cars {
		--fontsize-base: #{$vw_fontsize_mobile};
		font-size: calc(var(--fontsize-base) / #{$vw_design_mobile} * 100vw);
		padding: rem(30) rem(16);

		&__container {
			padding: 0 rem(16);
		}

		&__heading {
			font-size: rem(20);
		}

		&__card {
			width: rem(260);
		}

		&__view-all {
			font-size: rem(14);
			line-height: rem(14);
		}

		&__icon {
			width: rem(14);
			height: rem(14);
		}
	}
}
</style> 